import { useState } from 'react';
import { getBackendDetails } from '@/utils/getBackendDetails';
import { User } from '@/generated/graphql';
import { useJWT } from './useAuth';

interface QuickSignupResponse {
  success: boolean;
  user: User;
  accessToken: string;
}

export function useQuickSignup() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { storeAccessToken } = useJWT();

  const quickSignup = async (name: string) => {
    setIsLoading(true);
    setError(null);

    const { hostname, httpProtocol } = getBackendDetails();
    const quickSignupUrl = `${httpProtocol}://${hostname}/auth/quicksignup`;

    try {
      const response = await fetch(quickSignupUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
        }),
        credentials: 'include', // Important for cookies
      });

      const data: QuickSignupResponse = await response.json();

      if (!response.ok) {
        throw new Error('Quick signup failed');
      }

      // Store the access token
      if (data.success && data.accessToken) {
        storeAccessToken(data.accessToken);
      }

      return data;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred during quick signup',
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    quickSignup,
    error,
    isLoading,
  };
}
